import { useEffect, useState, Fragment } from "react";
import { Gift, Users, Wallet, TrendingUp, Banknote, CheckCircle2, XCircle, Clock3, Loader2, Mail, ChevronDown, ChevronRight, Trophy, ChevronLeft, BadgeCheck, Settings, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatAmount } from "@/lib/constants";

const LEAGUE_TIER_META = {
  bronze: { emoji: "🥉", label: "Bronze", color: "text-amber-700 bg-amber-50" },
  argent: { emoji: "🥈", label: "Argent", color: "text-slate-600 bg-slate-100" },
  or: { emoji: "🥇", label: "Or", color: "text-yellow-700 bg-yellow-50" },
};
const LEAGUE_STATUS_META = {
  pending_validation: { label: "En revue", icon: Clock3, color: "text-amber-700 bg-amber-50" },
  validated: { label: "Validée", icon: CheckCircle2, color: "text-blue-700 bg-blue-50" },
  paid: { label: "Payée", icon: Banknote, color: "text-emerald-700 bg-emerald-50" },
  rejected: { label: "Refusée", icon: XCircle, color: "text-red-700 bg-red-50" },
};

const CHANNEL_LABELS = {
  airtel_gabon: "Airtel Gabon",
  moov_gabon: "Moov Gabon",
  tmoney_togo: "T-Money Togo",
  moov_togo: "Moov Togo",
  mtn_rwanda: "MTN Rwanda",
  tigo_rwanda: "Tigo Rwanda",
  airtel_rwanda: "Airtel Rwanda",
  paypal: "PayPal",
  wero: "Wero",
};

export default function AdminReferrals() {
  const [tab, setTab] = useState("parrains");
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get("/admin/referrals"),
        api.get("/admin/referral-payouts"),
      ]);
      setRefs(r.data || []);
      setPayouts(p.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="admin-referrals-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Gift size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Parrainage</h1>
          <p className="text-sm text-gray-500">Suivi des parrains, filleuls et demandes de virement.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <TabBtn active={tab === "parrains"} onClick={() => setTab("parrains")} count={refs.length} data-testid="tab-parrains">
          Parrains & filleuls
        </TabBtn>
        <TabBtn active={tab === "demandes"} onClick={() => setTab("demandes")} count={payouts.filter(p => p.status === "pending").length} data-testid="tab-demandes">
          Demandes de virement
        </TabBtn>
        <TabBtn active={tab === "historique"} onClick={() => setTab("historique")} count={payouts.filter(p => ["paid", "rejected"].includes(p.status)).length} data-testid="tab-historique">
          Historique virements
        </TabBtn>
        <TabBtn active={tab === "ligues"} onClick={() => setTab("ligues")} data-testid="tab-ligues">
          <Trophy size={13} className="inline -mt-0.5 mr-1" /> Ligues mensuelles
        </TabBtn>
        <TabBtn active={tab === "parametres"} onClick={() => setTab("parametres")} data-testid="tab-parametres">
          <Settings size={13} className="inline -mt-0.5 mr-1" /> Paramètres
        </TabBtn>
      </div>

      {tab === "ligues" ? (
        <LeaguesTable />
      ) : tab === "parametres" ? (
        <ReferralConfigPanel />
      ) : loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : tab === "parrains" ? (
        <ParrainsTable refs={refs} />
      ) : tab === "demandes" ? (
        <PayoutsTable payouts={payouts.filter((p) => p.status === "pending" || p.status === "approved")} onChange={refresh} editable />
      ) : (
        <PayoutsTable payouts={payouts.filter((p) => p.status === "paid" || p.status === "rejected")} onChange={refresh} />
      )}
    </div>
  );
}

function shiftMonth(ym, delta) {
  let [y, m] = ym.split("-").map(Number);
  m += delta;
  if (m < 1) { m = 12; y -= 1; }
  if (m > 12) { m = 1; y += 1; }
  return `${y}-${String(m).padStart(2, "0")}`;
}

function monthLabel(ym) {
  try { return new Date(`${ym}-01T00:00:00Z`).toLocaleDateString("fr-FR", { month: "long", year: "numeric", timeZone: "UTC" }); }
  catch (_e) { return ym; }
}

function defaultPrevMonth() {
  const now = new Date();
  const y = now.getUTCFullYear(), m = now.getUTCMonth(); // 0-indexed, so this IS previous month's 1-indexed value
  const py = m === 0 ? y - 1 : y;
  const pm = m === 0 ? 12 : m;
  return `${py}-${String(pm).padStart(2, "0")}`;
}

function LeaguesTable() {
  const [month, setMonth] = useState(defaultPrevMonth());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async (ym) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leagues", { params: { month: ym } });
      setRows(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(month); /* eslint-disable-next-line */ }, [month]);

  const decide = async (id, status, extra) => {
    try {
      await api.patch(`/admin/leagues/${id}`, { status, ...extra });
      toast.success("Statut mis à jour");
      refresh(month);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="leagues-table-wrap">
      <div className="flex items-center justify-center gap-3 mb-4">
        <button onClick={() => setMonth((m) => shiftMonth(m, -1))} className="w-8 h-8 rounded-full border border-gray-200 grid place-items-center hover:bg-gray-50" data-testid="league-month-prev"><ChevronLeft size={14} /></button>
        <div className="text-sm font-semibold capitalize min-w-[140px] text-center" data-testid="league-month-label">{monthLabel(month)}</div>
        <button onClick={() => setMonth((m) => shiftMonth(m, 1))} className="w-8 h-8 rounded-full border border-gray-200 grid place-items-center hover:bg-gray-50" data-testid="league-month-next"><ChevronRight size={14} /></button>
      </div>

      {loading ? (
        <div className="sl-card text-center text-gray-500">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="sl-card text-center text-sm text-gray-500" data-testid="leagues-empty">Aucun parrain n&apos;a atteint une ligue ce mois-ci.</div>
      ) : (
        <div className="sl-card overflow-x-auto" data-testid="leagues-table">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3">Parrain</th>
                <th className="py-2 pr-3 text-right">Filleuls validés</th>
                <th className="py-2 pr-3">Ligue</th>
                <th className="py-2 pr-3 text-right">Récompense</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const tm = LEAGUE_TIER_META[r.tier] || {};
                const sm = LEAGUE_STATUS_META[r.status] || LEAGUE_STATUS_META.pending_validation;
                const StIcon = sm.icon;
                return (
                  <tr key={r.id} className="border-b border-gray-50" data-testid={`league-row-${r.id}`}>
                    <td className="py-3 pr-3">
                      <div className="text-xs flex items-center gap-1"><Mail size={10} /> {r.parrain_email}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{r.parrain_code}</div>
                    </td>
                    <td className="py-3 pr-3 text-right font-semibold">{r.filleuls_count}</td>
                    <td className="py-3 pr-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${tm.color}`}>{tm.emoji} {tm.label}</span>
                    </td>
                    <td className="py-3 pr-3 text-right font-semibold text-emerald-700">{formatAmount(r.reward_xaf, "XAF")}</td>
                    <td className="py-3 pr-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${sm.color}`}><StIcon size={10} /> {sm.label}</span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      {r.status === "paid" ? (
                        <span className="text-xs text-gray-400">{r.payment_ref ? `Réf. ${r.payment_ref}` : "—"}</span>
                      ) : (
                        <div className="inline-flex flex-wrap gap-1.5 justify-end">
                          {r.status === "pending_validation" && (
                            <button onClick={() => decide(r.id, "validated")} className="text-xs inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-2.5 py-1 hover:bg-blue-700" data-testid={`league-validate-${r.id}`}>
                              <BadgeCheck size={10} /> Valider
                            </button>
                          )}
                          {r.status === "validated" && (
                            <button
                              onClick={() => { const ref = window.prompt("Référence du virement effectué :", ""); if (ref !== null) decide(r.id, "paid", { payment_ref: ref }); }}
                              className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 hover:bg-emerald-700" data-testid={`league-pay-${r.id}`}
                            >
                              <Banknote size={10} /> Marquer payée
                            </button>
                          )}
                          {r.status !== "rejected" && (
                            <button onClick={() => decide(r.id, "rejected")} className="text-xs inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 px-2.5 py-1 hover:bg-red-50" data-testid={`league-reject-${r.id}`}>
                              <XCircle size={10} /> Refuser
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">
        Les récompenses sont calculées à partir des filleuls directs dont le compte a été KYC-vérifié durant le mois sélectionné (auto-rafraîchi, hors fraude détectée : auto-parrainage et doublons de téléphone exclus). Une fois validée ou payée, une ligne est figée et ne peut plus être recalculée.
      </p>
    </div>
  );
}

function ReferralConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoStartLocal, setPromoStartLocal] = useState("");
  const [promoEndLocal, setPromoEndLocal] = useState("");
  const [promoPercent, setPromoPercent] = useState(20);

  const toLocalInputValue = (isoUtc) => {
    if (!isoUtc) return "";
    const d = new Date(isoUtc);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const toUtcIso = (localValue) => (localValue ? new Date(localValue).toISOString() : null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/referral-config");
      setCfg(data);
      setPromoEnabled(!!data.promo_start_at);
      setPromoStartLocal(toLocalInputValue(data.promo_start_at));
      setPromoEndLocal(toLocalInputValue(data.promo_end_at));
      if (data.promo_discount_percent != null) setPromoPercent(data.promo_discount_percent);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (promoEnabled && (!promoStartLocal || !promoEndLocal)) {
      toast.error("Choisissez une date de début et de fin pour la promo.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        discount_percent: parseFloat(cfg.discount_percent),
        tier_threshold_xaf: parseFloat(cfg.tier_threshold_xaf),
        tier_commission_eur: parseFloat(cfg.tier_commission_eur),
        code_reusable: !!cfg.code_reusable,
        message_template: cfg.message_template,
      };
      if (promoEnabled) {
        payload.promo_discount_percent = parseFloat(promoPercent);
        payload.promo_start_at = toUtcIso(promoStartLocal);
        payload.promo_end_at = toUtcIso(promoEndLocal);
      } else {
        payload.clear_promo = true;
      }
      const { data } = await api.patch("/admin/referral-config", payload);
      setCfg(data);
      setPromoStartLocal(toLocalInputValue(data.promo_start_at));
      setPromoEndLocal(toLocalInputValue(data.promo_end_at));
      toast.success("Paramètres de parrainage mis à jour — la section parrainage se met à jour pour tous.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
    setSaving(false);
  };

  if (loading || !cfg) return <div className="sl-card text-center text-gray-500">Chargement…</div>;

  const previewMessage = (cfg.message_template || "")
    .replace(/\{code\}/g, "EXEMPLE123")
    .replace(/\{lien\}/g, `${window.location.origin}/register?ref=EXEMPLE123`)
    .replace(/\{reduction\}/g, String(promoEnabled ? promoPercent : (cfg.discount_percent ?? "")));

  return (
    <div className="sl-card max-w-2xl" data-testid="referral-config-panel">
      <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-4">Paramètres du programme de parrainage</div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="sl-label text-xs">Réduction sur les frais (%)</label>
          <input
            type="number" min={0} max={100} step={0.5}
            value={cfg.discount_percent}
            onChange={(e) => setCfg({ ...cfg, discount_percent: e.target.value })}
            className="sl-input" data-testid="config-discount-percent"
          />
          <p className="text-[11px] text-gray-400 mt-1">Appliquée sur les corridors Gabon → France et Gabon → Rwanda.</p>
        </div>
        <div>
          <label className="sl-label text-xs">Récompense parrain (€)</label>
          <input
            type="number" min={0} step={0.5}
            value={cfg.tier_commission_eur}
            onChange={(e) => setCfg({ ...cfg, tier_commission_eur: e.target.value })}
            className="sl-input" data-testid="config-tier-commission"
          />
          <p className="text-[11px] text-gray-400 mt-1">Gagnée à chaque palier de volume atteint.</p>
        </div>
        <div>
          <label className="sl-label text-xs">Palier de volume (FCFA)</label>
          <input
            type="number" min={1000} step={1000}
            value={cfg.tier_threshold_xaf}
            onChange={(e) => setCfg({ ...cfg, tier_threshold_xaf: e.target.value })}
            className="sl-input" data-testid="config-tier-threshold"
          />
          <p className="text-[11px] text-gray-400 mt-1">Volume cumulé des filleuls nécessaire pour débloquer la récompense.</p>
        </div>
        <div className="flex items-center gap-2 mt-6">
          <input
            type="checkbox"
            id="code-reusable"
            checked={!!cfg.code_reusable}
            onChange={(e) => setCfg({ ...cfg, code_reusable: e.target.checked })}
            className="w-4 h-4"
            data-testid="config-code-reusable"
          />
          <label htmlFor="code-reusable" className="text-sm">Un code peut être utilisé par plusieurs filleuls</label>
        </div>
      </div>

      <div className="mt-5">
        <label className="sl-label text-xs">Message de parrainage (affiché et partagé par les clients)</label>
        <textarea
          rows={4}
          value={cfg.message_template}
          onChange={(e) => setCfg({ ...cfg, message_template: e.target.value })}
          className="sl-input font-sans"
          data-testid="config-message-template"
        />
        <p className="text-[11px] text-gray-400 mt-1">
          Variables disponibles : <code className="font-mono">{"{code}"}</code>, <code className="font-mono">{"{lien}"}</code>, <code className="font-mono">{"{reduction}"}</code> — remplacées automatiquement par les vraies valeurs de chaque client.
        </p>
        <div className="mt-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3" data-testid="config-message-preview">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1.5">Aperçu — ce que verra le client</div>
          <p className="text-sm text-gray-700 whitespace-pre-line">{previewMessage || "—"}</p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="config-promo-block">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="promo-enabled"
            checked={promoEnabled}
            onChange={(e) => setPromoEnabled(e.target.checked)}
            className="w-4 h-4"
            data-testid="config-promo-enabled"
          />
          <label htmlFor="promo-enabled" className="text-sm font-semibold text-amber-900">Programmer une réduction temporaire</label>
          {cfg.promo_active && (
            <span className="ml-auto text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-0.5" data-testid="config-promo-active-badge">
              Promo active
            </span>
          )}
        </div>
        {promoEnabled && (
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="sl-label text-xs">Réduction promo (%)</label>
              <input
                type="number" min={0} max={100} step={0.5}
                value={promoPercent}
                onChange={(e) => setPromoPercent(e.target.value)}
                className="sl-input" data-testid="config-promo-percent"
              />
            </div>
            <div>
              <label className="sl-label text-xs">Début</label>
              <input
                type="datetime-local"
                value={promoStartLocal}
                onChange={(e) => setPromoStartLocal(e.target.value)}
                className="sl-input" data-testid="config-promo-start"
              />
            </div>
            <div>
              <label className="sl-label text-xs">Fin</label>
              <input
                type="datetime-local"
                value={promoEndLocal}
                onChange={(e) => setPromoEndLocal(e.target.value)}
                className="sl-input" data-testid="config-promo-end"
              />
            </div>
          </div>
        )}
        <p className="text-[11px] text-amber-800/80 mt-2">
          Pendant cette période, le taux promo remplace automatiquement la réduction par défaut partout sur le site, puis revient seul au taux normal à la fin.
        </p>
      </div>

      <button onClick={save} disabled={saving} className="sl-btn-primary mt-5 disabled:opacity-60" data-testid="config-save-btn">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
      </button>
    </div>
  );
}

function TabBtn({ active, onClick, children, count, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2 transition ${
        active
          ? "bg-[var(--sl-primary)] text-white shadow"
          : "bg-white border border-gray-200 text-gray-700 hover:border-[var(--sl-primary)]/40"
      }`}
      {...rest}
    >
      {children}
      {typeof count === "number" && <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 ${active ? "bg-white/20" : "bg-gray-100 text-gray-600"}`}>{count}</span>}
    </button>
  );
}

function ParrainsTable({ refs }) {
  const [expanded, setExpanded] = useState(null);
  const [txs, setTxs] = useState({});

  if (refs.length === 0) return <div className="sl-card text-center text-sm text-gray-500" data-testid="parrains-empty">Aucun parrainage utilisé pour l&apos;instant.</div>;

  const toggle = async (code) => {
    if (expanded === code) { setExpanded(null); return; }
    setExpanded(code);
    if (!txs[code]) {
      try {
        const { data } = await api.get(`/admin/referrals/${encodeURIComponent(code)}/transactions`);
        setTxs((prev) => ({ ...prev, [code]: data || [] }));
      } catch (_e) { /* ignore */ }
    }
  };

  return (
    <div className="sl-card overflow-x-auto" data-testid="parrains-table">
      <table className="w-full min-w-[700px] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
            <th className="py-2 pr-3"></th>
            <th className="py-2 pr-3">Parrain</th>
            <th className="py-2 pr-3">Code</th>
            <th className="py-2 pr-3 text-right"><Users size={13} className="inline" /> Filleuls</th>
            <th className="py-2 pr-3 text-right"><TrendingUp size={13} className="inline" /> Tx</th>
            <th className="py-2 pr-3 text-right"><Wallet size={13} className="inline" /> Gains total</th>
            <th className="py-2 pr-3 text-right">Disponible</th>
          </tr>
        </thead>
        <tbody>
          {refs.map((r) => (
            <Fragment key={r.code}>
              <tr className="border-b border-gray-50 hover:bg-gray-50/60 cursor-pointer" onClick={() => toggle(r.code)} data-testid={`parrain-row-${r.code}`}>
                <td className="py-3 pr-3 text-gray-400">{expanded === r.code ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                <td className="py-3 pr-3">
                  <div className="font-medium flex items-center gap-1.5">
                    <Mail size={11} className="text-gray-400" /> {r.parrain_email || <span className="italic text-gray-400">anonyme</span>}
                  </div>
                </td>
                <td className="py-3 pr-3 font-mono text-xs">{r.code}</td>
                <td className="py-3 pr-3 text-right font-semibold">{r.filleuls}</td>
                <td className="py-3 pr-3 text-right">{r.tx_count}</td>
                <td className="py-3 pr-3 text-right font-semibold text-emerald-700">{formatAmount(r.gains_total_xaf, "XAF")}</td>
                <td className="py-3 pr-3 text-right">
                  <span className={`font-semibold ${r.gains_available_xaf > 0 ? "text-[var(--sl-primary)]" : "text-gray-400"}`}>
                    {formatAmount(r.gains_available_xaf, "XAF")}
                  </span>
                </td>
              </tr>
              {expanded === r.code && (
                <tr>
                  <td colSpan={7} className="bg-[var(--sl-bg-alt)] p-4">
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-2">Transactions parrainées</div>
                    {(txs[r.code] || []).length === 0 ? (
                      <div className="text-xs text-gray-500">Aucune transaction trouvée.</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {(txs[r.code] || []).map((t) => (
                          <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 text-xs bg-white rounded-lg px-3 py-2 border border-gray-100">
                            <div>
                              <span className="font-mono font-semibold">{t.code}</span>
                              <span className="text-gray-400"> · </span>
                              <span>{t.sender_name}</span>
                              <span className="text-gray-400"> → </span>
                              <span>{t.beneficiary_name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-gray-500">{formatAmount(t.amount_xof, "XAF")}</span>
                              <span className="font-semibold text-emerald-700">+ {formatAmount(t.referral?.commission_xaf || 0, "XAF")}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutsTable({ payouts, onChange, editable }) {
  if (payouts.length === 0) return <div className="sl-card text-center text-sm text-gray-500" data-testid="payouts-empty">Aucune demande dans cette catégorie.</div>;
  return (
    <div className="sl-card overflow-x-auto">
      <table className="w-full min-w-[800px] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-gray-100">
            <th className="py-2 pr-3">Parrain</th>
            <th className="py-2 pr-3">Montant</th>
            <th className="py-2 pr-3">Canal</th>
            <th className="py-2 pr-3">Bénéficiaire</th>
            <th className="py-2 pr-3">Date</th>
            <th className="py-2 pr-3">Statut</th>
            <th className="py-2 pr-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {payouts.map((p) => (
            <PayoutRow key={p.id} p={p} editable={editable} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PayoutRow({ p, editable, onChange }) {
  const [acting, setActing] = useState(null);
  const channel = CHANNEL_LABELS[p.channel] || p.channel;

  const statusMap = {
    pending: { icon: Clock3, label: "En attente", color: "text-amber-700 bg-amber-50" },
    approved: { icon: CheckCircle2, label: "Approuvée", color: "text-blue-700 bg-blue-50" },
    paid: { icon: CheckCircle2, label: "Payée", color: "text-emerald-700 bg-emerald-50" },
    rejected: { icon: XCircle, label: "Refusée", color: "text-red-700 bg-red-50" },
  };
  const st = statusMap[p.status] || statusMap.pending;
  const Ic = st.icon;

  const decide = async (status) => {
    setActing(status);
    try {
      await api.patch(`/admin/referral-payouts/${p.id}`, { status });
      toast.success("Statut mis à jour");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setActing(null);
    }
  };

  return (
    <tr className="border-b border-gray-50" data-testid={`payout-row-${p.id}`}>
      <td className="py-3 pr-3">
        <div className="text-xs flex items-center gap-1"><Mail size={10} /> {p.parrain_email}</div>
        <div className="text-[10px] text-gray-400 font-mono">{p.parrain_code}</div>
      </td>
      <td className="py-3 pr-3 font-semibold">{formatAmount(p.amount_xaf, "XAF")}</td>
      <td className="py-3 pr-3 text-xs">{channel}</td>
      <td className="py-3 pr-3 text-xs">
        <div>{p.holder_name}</div>
        <div className="text-[10px] text-gray-500 font-mono">{p.account}</div>
      </td>
      <td className="py-3 pr-3 text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</td>
      <td className="py-3 pr-3">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${st.color}`}>
          <Ic size={10} /> {st.label}
        </span>
      </td>
      <td className="py-3 pr-3 text-right">
        {editable ? (
          <div className="inline-flex flex-wrap gap-1.5 justify-end">
            {p.status === "pending" && (
              <button onClick={() => decide("approved")} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full bg-blue-600 text-white px-2.5 py-1 hover:bg-blue-700 disabled:opacity-60" data-testid={`payout-approve-${p.id}`}>
                {acting === "approved" ? <Loader2 size={10} className="animate-spin" /> : <CheckCircle2 size={10} />} Approuver
              </button>
            )}
            <button onClick={() => decide("paid")} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 hover:bg-emerald-700 disabled:opacity-60" data-testid={`payout-paid-${p.id}`}>
              {acting === "paid" ? <Loader2 size={10} className="animate-spin" /> : <Banknote size={10} />} Marquer payée
            </button>
            <button onClick={() => decide("rejected")} disabled={!!acting} className="text-xs inline-flex items-center gap-1 rounded-full border border-red-200 text-red-700 px-2.5 py-1 hover:bg-red-50 disabled:opacity-60" data-testid={`payout-reject-${p.id}`}>
              {acting === "rejected" ? <Loader2 size={10} className="animate-spin" /> : <XCircle size={10} />} Refuser
            </button>
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}
